import { Trans } from '@lingui/react/macro';
import { type Options, type Point, type Series, type SeriesAreaOptions } from 'highcharts';
import { useEffect, useState } from 'react';

import { type FlareChart } from '@/components/Flare';
import { useFlareContext } from '@/components/Flare/FlareContext';
import { Text } from '@/components/typography';

const MIN_LABEL_HEIGHT = 52;
const LABEL_DISTANCE = 15;

type Props = {
  width?: number;
};

type AreaSeries = Series[] & SeriesAreaOptions[];

type PositionDataType = {
  height: number;
  title: string;
  subtitle?: string;
  value: number;
};

type PointExtended = Point & {
  stackY?: number;
};

const buildOptions = (options: Options, onChartRedraw: (chart: FlareChart) => void): Options => {
  return {
    ...options,
    chart: {
      ...options.chart,
      events: {
        ...options.chart?.events,
        redraw: function (event) {
          options.chart?.events?.redraw?.call(this, event);
          onChartRedraw(this as FlareChart);
        },
      },
    },
  };
};

const TrendSummaryColumn = ({ width = 80 }: Props) => {
  const { id, registerChild } = useFlareContext();
  const [positionData, setPositionData] = useState<PositionDataType[] | undefined>();
  const [margin, setMargin] = useState(0);
  const [totalVisits, setTotalVisits] = useState(0);

  const handleRender = (chart: FlareChart) => {
    const series = (chart?.series as AreaSeries) || [];
    const yAxis = chart?.yAxis?.[0];
    const minYPixels = yAxis.toPixels(yAxis?.min ?? 0, true);
    let maxSeriesHeight = 0;
    let totalYVal = 0;
    let adjustedYDiff = 0;

    const posData = [...series]
      // Reverse because we want to start with smallest series for positioning adjustments
      .reverse()
      .filter((s) => s.visible)
      .map((s) => {
        const lastPoint = s.data?.at(-1) as PointExtended;
        const seriesTitle = s.options.custom?.seriesTitle ?? s.name;
        const seriesSubtitle = s.options.custom?.seriesSubtitle;
        const yVal = lastPoint?.y || 0;
        const yStackVal = lastPoint?.stackY || 0;
        let seriesHeight = minYPixels - yAxis.toPixels(yVal, true);

        if (s.visible) {
          maxSeriesHeight = Math.max(maxSeriesHeight, minYPixels - yAxis.toPixels(yStackVal, true));
          totalYVal += yVal;

          if (seriesHeight < MIN_LABEL_HEIGHT) {
            // how much height will we need to give from this smaller segment?
            adjustedYDiff += MIN_LABEL_HEIGHT - seriesHeight;
          } else if (adjustedYDiff > 0) {
            // how much height can we take from this larger segment?
            const available = Math.min(seriesHeight - MIN_LABEL_HEIGHT, adjustedYDiff);
            const seriesYDiff = Math.min(available, adjustedYDiff);
            adjustedYDiff -= seriesYDiff;
            seriesHeight -= seriesYDiff;
          }
        }

        return {
          height: seriesHeight,
          title: seriesTitle,
          subtitle: seriesSubtitle,
          value: yVal,
        };
      });

    setPositionData(posData.reverse());

    setMargin(chart.plotHeight - maxSeriesHeight);
    setTotalVisits(totalYVal);
  };

  useEffect(() => {
    registerChild(id, (options: Options) => buildOptions(options, handleRender));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="absolute top-20.5 right-0 bottom-13 flex flex-col"
      style={{ width: width - LABEL_DISTANCE }}
    >
      {totalVisits > 0 && (
        <>
          <Text variant="caption1" weight="semi-bold">
            <Trans>
              Total visits <br />
              {totalVisits}
            </Trans>
          </Text>
          <div className="flex h-97.5 flex-col justify-end" style={{ marginTop: margin }}>
            {positionData?.map((item) => (
              <div
                key={`${item.title}-${item.subtitle}`}
                className="flex flex-col justify-center"
                style={{
                  height: Math.max(Math.round(item.height), 40),
                }}
              >
                <Text variant="caption1" color="grey" weight="semi-bold">
                  {item.title}
                </Text>
                {item.subtitle && (
                  <Text variant="caption1" color="grey">
                    {item.subtitle}
                  </Text>
                )}
                <Text variant="caption2">{item.value}</Text>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TrendSummaryColumn;
